import { compact } from "lodash";
import {
  ComplianceProgramParticipantStatus,
  UnblockedBy,
} from "types/graphql-schema";

import {
  ComplianceProgramReportingEntityTableKeyName,
  ComplianceProgramReportingEntityTableParticipantStatusWithNominations,
} from "./compliance-program-progress-entity-table";

export const complianceProgramReportingTableColsClassName =
  "w-36 max-w-36 2xl:w-48 2xl:max-w-48 shrink-0";
// Wider than a step column: on a person's row it holds the name plus two 32px slots, the
// review package link and the row's menu.
export const complianceProgramReportingTableColNameClassName =
  "w-56 max-w-56 2xl:w-64 2xl:max-w-64 shrink-0 truncate";

const participantCanBeReminded =
  (keyName: ComplianceProgramReportingEntityTableKeyName) =>
  (
    participantStatus: ComplianceProgramReportingEntityTableParticipantStatusWithNominations,
  ) => {
    return (
      participantStatus[keyName] ===
        ComplianceProgramParticipantStatus.NotStarted ||
      participantStatus[keyName] ===
        ComplianceProgramParticipantStatus.InProgress
    );
  };

export const filterParticipantsToBeReminded = (
  participantStatuses: ComplianceProgramReportingEntityTableParticipantStatusWithNominations[],
  keyName: ComplianceProgramReportingEntityTableKeyName,
) => {
  return participantStatuses
    .filter(participantCanBeReminded(keyName))
    .map((participantStatus) => participantStatus.user);
};

/**
 * The step row behind one Progress tab column, or null when the column has none.
 *
 * The server names each step row with the participant status field it describes, which
 * is the same string a column uses as its key, so a cell finds its own due date, its
 * lateness and its reason without a lookup table.
 */
export const getStepProgress = (
  participantStatus: ComplianceProgramReportingEntityTableParticipantStatusWithNominations,
  keyName: ComplianceProgramReportingEntityTableKeyName,
) => participantStatus.steps.find((step) => step.key === keyName) ?? null;

/**
 * What a Blocked cell says: the wait, and who can end it.
 *
 * The server decides who that is, from where the reader sits, with the same check the
 * calibration mutation makes. This only turns its answer into words. It names a role and
 * never a person, because calibration groups can be private.
 */
export const getBlockedStepText = (
  step: Pick<
    NonNullable<ReturnType<typeof getStepProgress>>,
    "blockedReason" | "unblockedBy"
  >,
  participantName: string,
) => {
  if (step.unblockedBy === UnblockedBy.Viewer) {
    return `Waiting for pre-calibration. You can mark ${participantName} as calibrated on the Results tab.`;
  }
  if (step.unblockedBy === UnblockedBy.CalibrationApproverOrHrAdmin) {
    return `Waiting for pre-calibration. A calibration approver or an HR admin must mark ${participantName} as calibrated first.`;
  }
  if (step.unblockedBy === UnblockedBy.HrAdmin) {
    return `Waiting for pre-calibration. An HR admin must mark ${participantName} as calibrated first.`;
  }
  return step.blockedReason;
};

/** The people whose work on this column is open and past its due date. */
export const filterOverdueParticipants = (
  participantStatuses: ComplianceProgramReportingEntityTableParticipantStatusWithNominations[],
  keyName: ComplianceProgramReportingEntityTableKeyName,
) =>
  participantStatuses.filter(
    (participantStatus) => getStepProgress(participantStatus, keyName)?.overdue,
  );

/**
 * When a column's step fell due, said once for the whole column.
 *
 * A due date belongs to the step, not to the person, so on an ordinary review every late
 * cell in a column would carry the same figure. Saying it nine times down a column tells
 * the reader nothing eight of those times, so it is said once, above the tables.
 *
 * An ongoing review is the exception: it starts each person on their own hire date, so
 * the same step falls due on a different day for each of them and no single date is
 * true. What is true there is the rule they all follow - 30 days after they started -
 * which is what `afterStartDays` carries. When even that differs, the column says
 * nothing rather than something vague.
 */
export type ColumnDueDate =
  | { varies: true; afterStartDays: number | null }
  | { varies: false; dueDate: string };

export const getColumnDueDate = (
  participantStatuses: ComplianceProgramReportingEntityTableParticipantStatusWithNominations[],
  keyName: ComplianceProgramReportingEntityTableKeyName,
): ColumnDueDate | null => {
  const steps = compact(
    participantStatuses.map((participantStatus) =>
      getStepProgress(participantStatus, keyName),
    ),
  ).filter((step) => step.dueDate);
  if (steps.length === 0) {
    return null;
  }
  const dueDates = new Set(steps.map((step) => step.dueDate));
  if (dueDates.size > 1) {
    const rules = new Set(steps.map((step) => step.dueAfterStartDays));
    return {
      varies: true,
      afterStartDays:
        rules.size === 1 ? (steps[0].dueAfterStartDays ?? null) : null,
    };
  }
  return {
    varies: false,
    dueDate: steps[0].dueDate as string,
  };
};
